import { useAtomValue } from "jotai";
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import type {
  ConversationMessagesResponse,
  FeedbackSnapshot,
} from "@go-novus/admin-api";
import { assistantHasFeedback } from "@go-novus/widget";
import {
  Avatar,
  Button,
  Icon,
  MessageBubble,
  Pill,
  Skeleton,
  ScrollArea,
  useToast,
} from "@go-novus/ui";

import {
  conversationMessagesAtom,
  readConversationsFailureMessage,
} from "#/features/conversations";
import { createMerchantFeedback } from "#/features/feedback/feedback-api";
import * as m from "#/paraglide/messages";
import {
  formatWorkspaceClock,
  formatWorkspaceDateTime,
} from "#/shared/lib/date";

import { InboxNotice } from "./inbox-notice";
import { ReplayMessage } from "./replay-message";

/** 店铺时区还没到手时不摆时间，免得先显示浏览器时区再跳一次。 */
function readReplayMeta(
  conversation: ConversationMessagesResponse["conversation"],
  timeZone: string | undefined,
): string | undefined {
  return timeZone === undefined
    ? undefined
    : m.inbox_replay_meta({
        last: formatWorkspaceDateTime(conversation.lastMessageAt, timeZone),
        started: formatWorkspaceDateTime(conversation.startedAt, timeZone),
      });
}

/** 占位气泡数：撑满一屏之内的高度，多了纯属让人等着看动画。 */
const SKELETON_BUBBLES = 4;

// 加载中和有内容共用这块滚动区，两边的内距和行距一样，切换会话时气泡不换位置。
const TRANSCRIPT = "flex min-h-0 flex-1 flex-col gap-3.5 px-6 pt-5.5 pb-6.5";

interface ReplayHeaderProps {
  children: ReactNode;
}

/** 加载中和有内容共用这一条：切换会话时头一行的边框和内距不动。 */
function ReplayHeader({ children }: ReplayHeaderProps) {
  return (
    <div className="flex min-h-17 flex-none items-center gap-3 border-b border-[var(--border-surface-default)] px-4.5 py-2.75">
      {children}
    </div>
  );
}

interface ReplayBodyProps {
  conversationId: string;
  timeZone: string | undefined;
}

/** 选中会话的完整回放：消息按接口返回的原始顺序渲染，不做归并和折叠。 */
function ReplayBody({ conversationId, timeZone }: ReplayBodyProps) {
  const replay = useAtomValue(conversationMessagesAtom(conversationId));
  const data = replay.data;
  const { add: addToast } = useToast();
  const feedback = useMemo(
    () => ({
      ...createMerchantFeedback("inbox"),
      onLikeError: (message: string) => {
        addToast({ data: { tone: "error" }, title: message });
      },
    }),
    [addToast],
  );
  const [feedbackSnapshots, setFeedbackSnapshots] = useState<
    Record<string, FeedbackSnapshot>
  >({});
  const feedbackIds = (data?.messages ?? [])
    .filter((message) => assistantHasFeedback(message))
    .map((message) => message.id)
    .join("\n");
  useEffect(() => {
    if (feedbackIds === "") return;
    let cancelled = false;
    void feedback.load(feedbackIds.split("\n")).then(
      (rows) => {
        if (cancelled) return;
        setFeedbackSnapshots(
          Object.fromEntries(rows.map((row) => [row.messageId, row])),
        );
      },
      () => undefined,
    );
    return () => {
      cancelled = true;
    };
  }, [feedback, feedbackIds]);
  const transcriptRef = useRef<HTMLDivElement>(null);
  const loaded = data !== undefined;
  const timed = timeZone !== undefined;

  // 打开会话先看最新一条。消息到手、店铺时区到手（每条多出一行时刻）都会把回放撑高，两次都要贴底。
  // 别把 data 放进依赖：窗口回到前台时会重新拉取，带来新消息就跟着滚，会把正在往上翻的商家拽回底部。
  useLayoutEffect(() => {
    const transcript = transcriptRef.current;
    if (transcript !== null) {
      transcript.scrollTop = transcript.scrollHeight;
    }
  }, [conversationId, loaded, timed]);
  // 失败一律出话：分类不出来的错误也不能把面板停在"加载中"。
  const failureMessage = replay.isError
    ? (readConversationsFailureMessage(replay.error) ??
      m.inbox_conversations_failure_unexpected())
    : null;

  if (failureMessage !== null) {
    return (
      <div className="flex min-h-0 min-w-0 flex-col justify-center">
        <InboxNotice heading={failureMessage} iconName="info" />
      </div>
    );
  }

  if (data === undefined) {
    // 不摆 EmptyState：那是终局态的形状，一闪而过的加载套上去，每切一次会话整块版式就跳一次。
    // 气泡也不铺骨架屏，宽窄由内容定、猜错了比空着还跳，见 Skeleton 的用法说明。
    return (
      <div className="flex min-h-0 min-w-0 flex-col">
        <ReplayHeader>
          <Skeleton width={36} height={36} />
          <div className="min-w-0 flex-1">
            <span className="block text-headline font-bold text-[var(--content-fg-strong)]">
              <Skeleton inline width={112} />
            </span>
            <span className="mt-1 block text-micro text-[var(--content-fg-subtle)]">
              <Skeleton inline width={208} />
            </span>
          </div>
        </ReplayHeader>
        <span className="sr-only" role="status">
          {m.inbox_replay_loading()}
        </span>
        {/* 占位气泡对读屏隐藏：上面那行 status 已经说清在等什么，
            再报一遍同名的空列表只会让人以为回放已经到了。 */}
        <ScrollArea
          height="auto"
          className="flex-1"
          contentClassName="w-full min-w-0!"
        >
          <ul aria-hidden className={TRANSCRIPT}>
            {Array.from({ length: SKELETON_BUBBLES }, (_, row) => (
              <li className="flex flex-col" key={row}>
                <MessageBubble
                  loading
                  role={row % 2 === 0 ? "ai" : "shopper"}
                />
              </li>
            ))}
          </ul>
        </ScrollArea>
      </div>
    );
  }

  const meta = readReplayMeta(data.conversation, timeZone);

  return (
    <div className="flex min-h-0 min-w-0 flex-col">
      <ReplayHeader>
        <span aria-hidden>
          <Avatar
            name={data.conversation.visitorLabel}
            size={36}
            shape="rounded"
          />
        </span>
        <div className="min-w-0 flex-1">
          <span className="block truncate text-headline font-bold text-[var(--content-fg-strong)]">
            {data.conversation.visitorLabel}
          </span>
          {meta !== undefined && (
            <span
              title={meta}
              className="mt-1 block text-micro text-[var(--content-fg-subtle)] @min-[44rem]:truncate"
            >
              {meta}
            </span>
          )}
        </div>
        <span className="flex shrink-0 items-center gap-1.5 @max-[44rem]:hidden text-micro text-[var(--content-fg-subtle)]">
          <Icon name="eye" size={13} />
          {m.inbox_replay_read_only()}
        </span>
        {data.conversation.handoff && (
          <Pill tone="review">{m.inbox_conversation_handoff()}</Pill>
        )}
      </ReplayHeader>
      <ScrollArea
        height="auto"
        className="flex-1"
        contentClassName="w-full min-w-0!"
        viewportRef={transcriptRef}
        label={m.inbox_replay_transcript_label()}
      >
        <ul
          aria-label={m.inbox_replay_transcript_label()}
          className={TRANSCRIPT}
        >
          {data.messages.map((message, index) => (
            <li className="flex flex-col" key={message.id}>
              <ReplayMessage
                groupStart={data.messages[index - 1]?.role !== message.role}
                feedback={
                  message.role === "assistant"
                    ? {
                        snapshot: feedbackSnapshots[message.id],
                        onLoadReasons: feedback.loadReasons,
                        onLikeError: feedback.onLikeError,
                        onSubmit: async (draft) => {
                          const record = await feedback.submit(
                            message.id,
                            draft,
                          );
                          setFeedbackSnapshots((current) => ({
                            ...current,
                            [message.id]: record,
                          }));
                          return record;
                        },
                      }
                    : undefined
                }
                timeZone={timeZone}
                message={message}
                sentAt={
                  timeZone === undefined
                    ? undefined
                    : formatWorkspaceClock(message.sentAt, timeZone)
                }
              />
            </li>
          ))}
        </ul>
      </ScrollArea>
    </div>
  );
}

export interface ConversationReplayProps extends ReplayBodyProps {
  onBack: () => void;
}

/**
 * 回列表的路只在窄屏需要，而且要摆在回放状态分支外面：回放拉不出来时里面只剩一句错误，
 * 按钮跟着藏起来的话商家就困在这一屏了。
 */
export function ConversationReplay({
  onBack,
  ...body
}: ConversationReplayProps) {
  return (
    <div className="flex min-h-0 min-w-0 flex-col gap-2.5">
      <div className="px-4 pt-3 @min-[44rem]:hidden">
        <Button
          icon={<Icon name="chevronLeft" size={16} />}
          onClick={onBack}
          size="sm"
          variant="quiet"
        >
          {m.inbox_replay_back()}
        </Button>
      </div>
      {/* 回放的每个状态分支套一层 grid 让它们一律撑满剩下的高度，
          省得四个分支各写一遍 flex-1。 */}
      <div className="grid min-h-0 min-w-0 flex-1">
        <ReplayBody {...body} />
      </div>
    </div>
  );
}
